import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { SliceType } from '~/enums/SliceType';
import { Uuid } from '~/types/Uuid';

import {
  DocumentEditorType,
  getTabEditingKey,
  DocumentEditorsState as State,
} from '../types/documentEditors';

const name = SliceType.DocumentEditors;

const initialState: State = { versions: {}, tabEditing: {} };

/**
 * This slice tracks, adds, removes editors (as an array) on document versions on specific document editors (e.g. ej2, wopi)
 * We use an array for instances where a user can have multiple sessions in the same editor (eg. wopi).
 */
export default createSlice({
  name,
  initialState,
  reducers: {
    addEditor: (
      state: State,
      action: PayloadAction<{
        documentEditor: DocumentEditorType;
        editor: Uuid;
        versionId: Uuid;
      }>,
    ) => {
      const { documentEditor, editor, versionId } = action.payload;

      if (!state.versions[versionId]) {
        state.versions[versionId] = {
          ej2: [],
          wopi: [],
          sharepoint: [],
        };
      }

      state.versions[versionId][documentEditor].push(editor);
    },
    removeEditor: (
      state: State,
      action: PayloadAction<{
        documentEditor: DocumentEditorType;
        editor: Uuid;
        versionId: Uuid;
      }>,
    ) => {
      const { documentEditor, editor, versionId } = action.payload;

      const index =
        state.versions?.[versionId]?.[documentEditor].indexOf(editor);
      if (index !== -1)
        state.versions?.[versionId]?.[documentEditor].splice(index, 1);
    },
    setEditors: (
      state: State,
      action: PayloadAction<{
        documentEditor: DocumentEditorType;
        editors: Array<Uuid>;
        versionId: Uuid;
      }>,
    ) => {
      const { documentEditor, editors, versionId } = action.payload;

      if (!state.versions[versionId]) {
        state.versions[versionId] = {
          ej2: [],
          wopi: [],
          sharepoint: [],
        };
      }

      state.versions[versionId][documentEditor] = editors;
    },
    setIsCurrentTabEditing: (
      state: State,
      action: PayloadAction<{
        documentEditor: DocumentEditorType;
        versionId: Uuid;
        value: boolean;
      }>,
    ) => {
      const { documentEditor, versionId, value } = action.payload;
      state.tabEditing[getTabEditingKey(versionId, documentEditor)] = value;
    },
  },
});
